/**
 * TEMPORARY HACK. Delete this entire folder.
 *
 * UniCredit Invest Lux asked for internal-audit accounts that look read only
 * and cannot change notes or NAV reconciliation status. There is no read-only
 * user type, and resources cannot express "view but do not edit" yet, so this
 * is keyed off the Raptor config `ucilux_auditor_read_only_funds`.
 *
 * A permanent solution has to use resources, not a config name. When that
 * exists, delete this folder and the call sites marked TEMPORARY HACK in:
 * - UserList.component.tsx (config selector column)
 * - uciluxUserConfigSelector.hack.tsx
 * - Notes.tsx
 * - ExportButton.tsx
 * - NavReconAuditStatusCell.tsx
 * and UCILUX_AUDITOR_READ_ONLY_CONFIGURATION in client_configurations.
 *
 * The config selector is a second temporary hack in this folder. UCI Lux
 * administrators need to move users between Raptor configs. Pages will
 * eventually be granted through resources, so the selector is UCI Lux only.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { MenuItem, Tooltip } from '@mui/material';
import FastAPIInstance from '../../services/risksystem3-public-api';
import { configNameSelector } from '../../redux/auth/selectors';

const UCILUX_CLIENT_NAME = 'ucilux';
export const UCILUX_AUDITOR_READ_ONLY_CONFIG = 'ucilux_auditor_read_only_funds';

/** Shared with the config selector so both read the same assignment cache. */
export const UCILUX_LEGACY_CONFIGS_QUERY_KEY = 'ucilux-auditor-read-only-hack';

export const isUciluxClient = (clientName?: string | null): boolean =>
  clientName === UCILUX_CLIENT_NAME;

const NOTES_READ_ONLY_MESSAGE =
  'This account is read only. You can view existing notes, but you cannot add new ones.';

const NAV_RECON_READ_ONLY_MESSAGE =
  'This account is read only. You can view the audit status, but you cannot approve or reject it.';

export const isUciluxAuditorReadOnlyConfig = (
  configName?: string | null,
): boolean => configName === UCILUX_AUDITOR_READ_ONLY_CONFIG;

/** True when the signed-in user is on the ucilux auditor config. */
export const useIsUciluxAuditorReadOnly = (): boolean => {
  const configName = useSelector(configNameSelector);
  return isUciluxAuditorReadOnlyConfig(configName);
};

type LegacyConfigAssignment = {
  user_id?: string;
  config_name?: string;
  created_timestamp?: string;
};

const latestConfigNameByUserId = (
  assignments: LegacyConfigAssignment[],
): Record<string, string> => {
  const latestByUser = new Map<string, LegacyConfigAssignment>();

  assignments.forEach((assignment) => {
    if (!assignment.user_id || !assignment.config_name) return;

    const current = latestByUser.get(assignment.user_id);
    const nextTimestamp = assignment.created_timestamp ?? '';
    const currentTimestamp = current?.created_timestamp ?? '';

    if (!current || nextTimestamp > currentTimestamp) {
      latestByUser.set(assignment.user_id, assignment);
    }
  });

  return Object.fromEntries(
    Array.from(latestByUser.entries()).map(([userId, assignment]) => [
      userId,
      assignment.config_name as string,
    ]),
  );
};

/** Config name per user id. Empty for every client other than ucilux. */
export const useUciluxReadOnlyConfigByUserId = (
  clientId: string | undefined,
  clientName: string | null | undefined,
): {
  configByUserId: Record<string, string>;
  isReady: boolean;
  isError: boolean;
} => {
  const isUcilux = isUciluxClient(clientName);

  const { data, isFetched, isError } = useQuery({
    queryKey: [UCILUX_LEGACY_CONFIGS_QUERY_KEY, clientId],
    queryFn: async () => {
      const response = await FastAPIInstance.get<LegacyConfigAssignment[]>(
        `/v1/raptor_legacy_configs/${clientId}`,
      );
      const assignments = Array.isArray(response.data) ? response.data : [];
      return latestConfigNameByUserId(assignments);
    },
    enabled: isUcilux && !!clientId,
  });

  return {
    configByUserId: isUcilux ? (data ?? {}) : {},
    isReady: !isUcilux || !clientId || isFetched,
    isError: isUcilux && !!clientId && isError,
  };
};

/** Replaces the note composer for an auditor. Otherwise renders children. */
export const UciluxAuditorNotesWriteGate = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const blocked = useIsUciluxAuditorReadOnly();

  if (!blocked) return <>{children}</>;

  return (
    <div className="w-full border-b border-slate-200 bg-slate-50 px-4 py-3">
      <p className="m-0 text-xs leading-relaxed text-gray-600">
        {NOTES_READ_ONLY_MESSAGE}
      </p>
    </div>
  );
};

/** Save to Note. Disabled, with an explanation, for an auditor. */
export const UciluxAuditorSaveToNoteMenuItem = ({
  onSave,
}: {
  onSave: () => void;
}) => {
  const blocked = useIsUciluxAuditorReadOnly();

  if (!blocked) {
    return (
      <MenuItem onClick={onSave} key="notes">
        Save to Note
      </MenuItem>
    );
  }

  return (
    <Tooltip title={NOTES_READ_ONLY_MESSAGE} placement="left">
      <span>
        <MenuItem disabled key="notes">
          Save to Note
        </MenuItem>
      </span>
    </Tooltip>
  );
};

/** Shown in place of the NAV reconciliation comment box for an auditor. */
export const UciluxAuditorNavReconWriteNotice = () => {
  const blocked = useIsUciluxAuditorReadOnly();
  if (!blocked) return null;

  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm leading-relaxed text-gray-700">
      {NAV_RECON_READ_ONLY_MESSAGE}
    </div>
  );
};

/** Forces approve/reject disabled, with the auditor explanation, when blocked. */
export const blockUciluxAuditorNavReconAction = (
  blocked: boolean,
  disabled: boolean,
  disabledReason: string,
): { disabled: boolean; disabledReason: string } =>
  blocked
    ? { disabled: true, disabledReason: NAV_RECON_READ_ONLY_MESSAGE }
    : { disabled, disabledReason };
